/**
 * Build menu — DOM palette of constructible buildings.
 * Lists the faction's implemented Echelon I buildings from the data mirror;
 * entries disable with a tooltip when unaffordable or when `requires` is unmet.
 */
import type { BuildingDef } from "../data/types";
import type { Economy } from "../sim/economy";
import { buildingIcon, iconImg } from "./icons";
import {
  costOf,
  missingRequirements,
  type PlacedBuilding,
} from "../sim/buildings";

function costLabel(cost: Record<string, number>): string {
  const parts = Object.entries(cost).map(([k, v]) => `${v} ${k}`);
  return parts.length > 0 ? parts.join(", ") : "free";
}

/** 44px touch targets on coarse-pointer (phone) devices. */
const TOUCH_PAD =
  typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: coarse)").matches
    ? "12px 10px"
    : "6px 8px";

export class BuildMenu {
  private el: HTMLDivElement;
  onBlueprint: ((def: BuildingDef | null) => void) | null = null;
  private activeId: string | null = null;

  constructor(private defs: BuildingDef[]) {
    this.el = document.createElement("div");
    this.el.id = "build-menu";
    this.el.style.cssText =
      "position:fixed;left:12px;top:64px;max-width:230px;max-height:70vh;overflow:auto;" +
      "background:rgba(10,10,12,0.82);border:1px solid #3a2f22;border-radius:8px;" +
      "padding:8px;font-size:13px;";
    document.body.appendChild(this.el);
    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.setBlueprint(null);
    });
  }

  setBlueprint(def: BuildingDef | null): void {
    this.activeId = def?.id ?? null;
    this.onBlueprint?.(def);
    this.refreshLastArgs();
  }

  /** True while a building blueprint is being placed (Escape cancels it). */
  get placing(): boolean {
    return this.activeId !== null;
  }

  /** Replace the buildable list (e.g. after an echelon advance). */
  setDefs(defs: BuildingDef[]): void {
    this.defs = defs;
    this.refreshLastArgs();
  }

  private lastEconomy: Economy | null = null;
  private lastPlaced: PlacedBuilding[] = [];
  private lastResearched: ReadonlySet<string> | undefined = undefined;

  refresh(
    economy: Economy,
    placed: PlacedBuilding[],
    researched?: ReadonlySet<string>,
  ): void {
    this.lastEconomy = economy;
    this.lastPlaced = placed;
    this.lastResearched = researched;
    this.refreshLastArgs();
  }

  private refreshLastArgs(): void {
    const economy = this.lastEconomy;
    if (!economy) return;
    const placed = this.lastPlaced;
    const researched = this.lastResearched;
    const html = this.defs
      .map((def) => {
        const cost = costOf(def);
        const missing = missingRequirements(def, placed, researched);
        const afford = economy.canAfford(cost);
        const disabled = missing.length > 0 || !afford;
        const reason = missing.length > 0
          ? `Requires: ${missing.join(", ")}`
          : !afford
            ? "Insufficient resources"
            : def.extra["description"] ?? def.name_en;
        const active = this.activeId === def.id;
        return (
          `<button data-id="${def.id}" ${disabled ? "disabled" : ""} title="${reason}" ` +
          `style="display:block;width:100%;text-align:left;margin:4px 0;padding:${TOUCH_PAD};` +
          `background:${active ? "#5a3a1a" : "#1a1c20"};color:#e8e2d4;` +
          `border:1px solid ${active ? "#b98b3a" : "#3a2f22"};border-radius:5px;` +
          `cursor:${disabled ? "not-allowed" : "pointer"};opacity:${disabled ? 0.45 : 1};">` +
          `<b>${iconImg(buildingIcon(def.id), def.name_en, 20)}${def.name_en}</b><br/><span style="color:#9a917f;font-size:12px;">${costLabel(cost)}</span></button>`
        );
      })
      .join("");
    this.el.innerHTML =
      `<div style="color:#9a917f;margin-bottom:4px;">BUILD — Echelon I` +
      (this.activeId ? `<br/><span style="color:#b98b3a;">Placing: ${this.activeId} (click ground, Esc cancels)</span>` : "") +
      `</div>${html}`;
    this.el.querySelectorAll("button").forEach((btn) => {
      btn.addEventListener("click", () => {
        const def = this.defs.find((d) => d.id === btn.getAttribute("data-id")) ?? null;
        this.setBlueprint(this.activeId === def?.id ? null : def);
      });
    });
  }
}
